import type { UserPermissions } from '../pages/users/types/permissions';

/**
 * Full client portal permissions — used for clients viewing their own portal.
 */
export const FULL_CLIENT_PORTAL_PERMISSIONS: Partial<UserPermissions> = {
  show_dashboard: true,
  show_appointments_tab: true,
  show_documents_tab: true,
  show_billing_tab: true,
  show_chat_tab: true,
  /** Controls visibility of the assessments dashboard section and assessment fill routes. */
  show_forms_tab: true,
};

/**
 * Guarantor (proxy) client portal permissions.
 * Guarantors cannot see messages. Documents, assessments, and billing are allowed per PRD.
 */
export const GUARANTOR_CLIENT_PORTAL_PERMISSIONS: Partial<UserPermissions> = {
  show_dashboard: true,
  show_appointments_tab: true,
  show_documents_tab: true,
  show_billing_tab: true,
  show_chat_tab: false,
  show_forms_tab: true,
};

/**
 * Billing-only portal permissions (e.g. guarantor with billing responsibility only).
 */
export const BILLING_ONLY_CLIENT_PORTAL_PERMISSIONS: Partial<UserPermissions> = {
  show_dashboard: true,
  show_appointments_tab: false,
  show_documents_tab: false,
  show_billing_tab: true,
  show_chat_tab: false,
  show_forms_tab: false,
};

/**
 * Participant-only portal permissions (e.g. family member participating in sessions).
 */
export const PARTICIPANT_ONLY_CLIENT_PORTAL_PERMISSIONS: Partial<UserPermissions> = {
  show_dashboard: true,
  show_appointments_tab: true,
  show_documents_tab: false,
  show_billing_tab: false,
  show_chat_tab: false,
  show_forms_tab: false,
};

/** Whether the current path is allowed for the given client-portal permission set. */
export function isClientPortalPathAllowed(
  pathname: string,
  perms: Partial<UserPermissions>,
): boolean {
  if (pathname === '/client-portal' || pathname === '/client-portal/') {
    return perms.show_dashboard !== false;
  }
  if (pathname.startsWith('/client-portal/documents')) {
    return perms.show_documents_tab !== false;
  }
  if (pathname.startsWith('/client-portal/assessments')) {
    return perms.show_forms_tab !== false;
  }
  if (pathname.startsWith('/client-portal/billing')) {
    return perms.show_billing_tab !== false;
  }
  if (pathname.startsWith('/client-portal/messages')) {
    return perms.show_chat_tab !== false;
  }
  if (pathname.startsWith('/client-portal/directory') || pathname.startsWith('/client-portal/intake')) {
    return perms.show_dashboard !== false;
  }
  return true;
}

export function defaultClientPortalHome(perms: Partial<UserPermissions>): string {
  if (perms.show_dashboard !== false) {
    return '/client-portal';
  }
  if (perms.show_billing_tab !== false) {
    return '/client-portal/billing';
  }
  return '/client-portal/billing';
}

export function clientPortalNavItemAllowed(
  href: string,
  perms: Partial<UserPermissions>,
): boolean {
  if (href === '/client-portal') {
    return perms.show_dashboard !== false;
  }
  if (href.startsWith('/client-portal/documents')) {
    return perms.show_documents_tab !== false;
  }
  if (href.startsWith('/client-portal/billing')) {
    return perms.show_billing_tab !== false;
  }
  if (href.startsWith('/client-portal/messages')) {
    return perms.show_chat_tab !== false;
  }
  return true;
}

/**
 * Returns the effective permissions to use for client portal navigation.
 *
 * When viewing a related (dependent) patient, use `GUARANTOR_CLIENT_PORTAL_PERMISSIONS`
 * (no in-app messages; dashboard, documents, assessments, and billing are allowed per PRD).
 */
export function getEffectiveClientPortalPermissions(
  basePermissions: Partial<UserPermissions>,
  isViewingRelatedClient: boolean,
): Partial<UserPermissions> {
  // TODO: Detect permission tier (billing-only, clinical, participant-only) from
  // ProjectMembership or AccessPolicy metadata. Currently defaults to GUARANTOR for all related persons.
  if (isViewingRelatedClient) {
    return GUARANTOR_CLIENT_PORTAL_PERMISSIONS;
  }
  return basePermissions;
}
